/** «Аналитика ОПТ» (`/wholesale`, право `wholesale_analytics`): продажи ОМЕГА ФАРМ сторонним
 *  покупателям по месяцу фактической отгрузки из витрины dwh `mart.wholesale_*` — KPI отгрузок
 *  периода с Δ к прошлому периоду, портфель заказов (к отгрузке — с датой отгрузки в периоде, позже —
 *  справкой; просрочено — все на дату снимка; с плановой маржой; виды не сложены), покрытие ценой значком; дерево (покупатель / товар /
 *  характеристика / месяц / заказ) и «По месяцам», карточки сущностей. Состояние — в адресе
 *  (`preset`, `from`, `to`, `cmp`, `tab`, `tp` / `lv` / `lvoff`, `tsort` / `tdir`, `card`).
 *  Источник правды — docs/wholesale-analytics.md. */
import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import type { TreeDim } from "@/api/wholesale";
import { activeLevels } from "@/lib/hierarchy";
import { cn } from "@/lib/utils";
import { CARD_BACK_STATE } from "../ozon-analytics/state";
import { Seg } from "../ozon-analytics/shared";
import {
  badLevels,
  CMP_MODES,
  cmpOutOfData,
  DEFAULT_CMP,
  encodeCard,
  incompleteMonthNote,
  legacyTabPatch,
  monthOf,
  monthsCount,
  periodLabel,
  presetRange,
  prevRange,
  PRESETS,
  readState,
  screenScope,
  DEFAULT_TAB,
  TABS,
  type Card,
  type CmpMode,
  type MonthRange,
  type PresetId,
} from "./calc";
import { CardPanel } from "./CardPanel";
import { AMBER, CoverageChip, ErrorBox, Excluded, Flags, Freshness, Portfolio, RED, ShippedKpis } from "./parts";
import { useWholesaleMeta, useWholesaleSummary } from "./queries";
import { MonthsTable } from "./Tables";
import { WholesaleTree } from "./TreeTable";
import { WAREHOUSE_BLOCK_ENABLED } from "../wh-ops/flag";
import { useOptWhOps } from "../wh-ops/queries";
import { WarehouseBlock } from "../wh-ops/WarehouseBlock";

const MAX_MONTHS = 36;

function Loading({ text }: { text: string }): React.ReactElement {
  return (
    <div role="status" className="rounded-md border bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
      {text}
    </div>
  );
}

function MonthInputs({ value, onApply }: { value: MonthRange; onApply: (r: MonthRange) => void }): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <input
        type="month"
        aria-label="первый месяц"
        className="h-8 rounded-md border bg-background px-2"
        value={value.from}
        onChange={(e) => e.target.value && onApply({ from: e.target.value, to: value.to < e.target.value ? e.target.value : value.to })}
      />
      —
      <input
        type="month"
        aria-label="последний месяц"
        className="h-8 rounded-md border bg-background px-2"
        value={value.to}
        onChange={(e) => e.target.value && onApply({ from: value.from > e.target.value ? e.target.value : value.from, to: e.target.value })}
      />
    </span>
  );
}

export function WholesalePage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const st = readState(params);
  const meta = useWholesaleMeta();
  const current = monthOf();

  const range: MonthRange | null = useMemo(() => {
    if (st.preset === "custom") return st.custom;
    return presetRange(st.preset, current, meta.data ?? null);
  }, [st.preset, st.custom, current, meta.data]);
  const tooLong = range !== null && monthsCount(range) > MAX_MONTHS;
  const active = tooLong ? null : range;
  const cmp = useMemo(() => (st.cmp === "prev" && active ? prevRange(active) : null), [st.cmp, active]);

  const summary = useWholesaleSummary(active, cmp);
  // справочный блок «Склад» — свой запрос: его ошибка показывается в блоке, раздел работает дальше
  const whOps = useOptWhOps(WAREHOUSE_BLOCK_ENABLED ? active : null);

  const patch = useCallback(
    (p: Record<string, string | null>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(p)) {
            if (v === null) next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: false },
      ),
    [setParams],
  );
  // старая ссылка tab=buyers|products — один раз переписать адрес на дерево (без новой записи истории)
  const legacyPatch = legacyTabPatch(params);
  const legacy = legacyPatch ? JSON.stringify(legacyPatch) : null;
  useEffect(() => {
    if (!legacy) return;
    const p = JSON.parse(legacy) as Record<string, string | null>;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, val] of Object.entries(p)) {
          if (val === null) next.delete(k);
          else next.set(k, val);
        }
        return next;
      },
      { replace: true },
    );
  }, [legacy, setParams]);
  const setPreset = (id: PresetId): void => {
    if (id === "custom") {
      const r = range ?? { from: current, to: current };
      patch({ preset: "custom", from: r.from, to: r.to });
    } else patch({ preset: id, from: null, to: null });
  };
  const open = useCallback((c: Card): void => patch({ card: encodeCard(c) }), [patch]);
  // из карточки — новая запись истории с пометкой: «Назад» в шапке карточки вернёт к прежней
  const openFromCard = useCallback(
    (c: Card): void =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("card", encodeCard(c));
          return next;
        },
        { replace: false, state: { [CARD_BACK_STATE]: true } },
      ),
    [setParams],
  );
  const location = useLocation();
  const navigate = useNavigate();
  const canBack = (location.state as Record<string, unknown> | null)?.[CARD_BACK_STATE] === true;
  const close = (): void => patch({ card: null });
  const setCmp = (m: CmpMode): void => patch({ cmp: m === DEFAULT_CMP ? null : m });

  const state = summary.data?.state ?? meta.data?.state ?? null;
  // уровни дерева (как в его запросах) — карточке узла для `/node`; адрес с чужими разрезами — null
  const treeLevels = useMemo(() => {
    const lv = activeLevels(st.hierarchy) as TreeDim[];
    return lv.length > 0 && badLevels(st.hierarchy.levels).length === 0 ? lv : null;
  }, [st.hierarchy]);
  // Δ ненадёжна: сравнение вне данных витрины (история с начала загрузки) — ⚠ у Δ и плашка
  // пока /meta грузится — диапазон ещё не известен (не «вне данных»); упала — предупреждение
  const cmpWarning = meta.isPending ? null : cmpOutOfData(cmp, meta.data ? meta.data.kinds.shipped : undefined);
  const incomplete = active && state ? incompleteMonthNote(active, cmp, state.sales_date_to) : null;

  return (
    <div className="mx-auto max-w-none space-y-4" data-testid="wholesale-page">
      <div>
        <h1 className="text-2xl font-bold">Аналитика ОПТ</h1>
        <p className="text-sm text-muted-foreground">
          ОМЕГА ФАРМ → сторонние покупатели (не свои юрлица), «Производство маркетплейсы ГП». Месяц — месяц
          фактической отгрузки; портфель заказов на дату снимка — отдельно от отгруженного: «К отгрузке» — заказы с
          датой отгрузки в периоде, «Просрочено» — все просроченные (не только за период).
        </p>
      </div>

      {st.errors.length > 0 && (
        <div role="alert" className={RED} data-testid="url-errors">
          {st.errors.join("; ")}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Seg value={st.preset} options={PRESETS} onChange={setPreset} label="Период" />
        {st.preset === "custom" && range && (
          <MonthInputs value={range} onApply={(r) => patch({ preset: "custom", from: r.from, to: r.to })} />
        )}
        {range && <span className="text-sm text-muted-foreground" data-testid="period">{periodLabel(range)}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Seg value={st.cmp} options={CMP_MODES} onChange={setCmp} label="Сравнение" />
        {cmp && (
          <span className="text-sm text-muted-foreground" data-testid="cmp-period">
            сравнение — {periodLabel(cmp)}
          </span>
        )}
      </div>
      {cmpWarning && (
        <div role="status" className={AMBER} data-testid="cmp-warning">
          Δ ненадёжна: {cmpWarning}. История витрины опта — с начала загрузки 1С.
        </div>
      )}
      {incomplete && (
        <div role="status" className={AMBER} data-testid="cmp-incomplete">
          {incomplete}.
        </div>
      )}

      {tooLong && (
        <div role="alert" className={RED} data-testid="too-long">
          Период {monthsCount(range!)} мес. — больше {MAX_MONTHS}: сузьте период.
        </div>
      )}
      {st.preset === "all" && meta.data && range === null && (
        <div role="status" className={RED} data-testid="all-empty">
          В витрине опта нет ни одной строки — «Весь период» не определить.
        </div>
      )}

      {meta.isError && !summary.data && <ErrorBox error={meta.error} what="состояние витрины опта" />}
      {summary.isError && <ErrorBox error={summary.error} what="сводку опта" />}

      {state && (
        <>
          <Freshness state={state} />
          <Excluded state={state} />
        </>
      )}

      {active && !summary.data && !summary.isError && <Loading text="Загружаем сводку опта…" />}

      {summary.data && (
        <>
          <ShippedKpis
            shipped={summary.data.totals.shipped}
            cmp={summary.data.cmp ? summary.data.cmp.shipped : undefined}
            warning={cmpWarning}
          />
          <Portfolio
            planned={summary.data.open_orders.planned}
            overdue={summary.data.open_orders.overdue}
            after={summary.data.open_orders.planned_after}
            before={summary.data.open_orders.planned_before}
            asOf={summary.data.state.orders_as_of}
            onOpen={(kind) => open({ type: "portfolio", kind })}
          />
          <div className="flex flex-wrap items-start gap-2">
            <CoverageChip
              scope={screenScope(summary.data)}
              onShowUnpriced={() => open({ type: "unpriced" })}
              onShowNoRate={() => open({ type: "norate" })}
            />
          </div>
          <Flags totals={summary.data.totals} />

          <div role="tablist" className="flex gap-1 border-b">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={st.tab === t.id}
                onClick={() => patch({ tab: t.id === DEFAULT_TAB ? null : t.id })}
                className={cn(
                  "-mb-px border-b-2 px-3 py-1.5 text-sm",
                  st.tab === t.id ? "border-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {st.tab === "months" && <MonthsTable months={summary.data.months} totals={summary.data.totals} onOpen={open} />}
          {st.tab === "tree" && active && (
            <WholesaleTree
              range={active}
              cmp={cmp}
              cmpWarning={cmpWarning}
              hierarchy={st.hierarchy}
              treePreset={st.treePreset}
              sort={st.treeSort}
              onPatch={patch}
              onCard={open}
            />
          )}
          <p className="text-xs text-muted-foreground">
            Выручка — с НДС (как в 1С), себестоимость — с НДС, склад производства — без НДС; маржа = выручка − себестоимость −
            склад (как маржа МП); у открытых заказов — плановая. «*» — часть строк без цены или суммы заказа: в сумму они
            не вошли (не нулём).
          </p>
        </>
      )}

      {WAREHOUSE_BLOCK_ENABLED && active && (
        <WarehouseBlock
          q={whOps}
          hint={
            <>
              Операции и стоимость склада производства по направлению «Опт» за месяцы периода — как на дашборде склада.
              Справочно: в маржу опта не входит (склад в марже — ставка склада закупочной цены по строкам, другой расчёт).
            </>
          }
        />
      )}

      {st.card && active && (
        <CardPanel
          card={st.card}
          range={active}
          treeLevels={treeLevels}
          cmp={cmp}
          cmpWarning={cmpWarning}
          asOf={state?.orders_as_of ?? null}
          onClose={close}
          onBack={canBack ? () => navigate(-1) : undefined}
          onOpen={openFromCard}
        />
      )}
    </div>
  );
}
